import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Round it off for an avatar placeholder. */
  circle?: boolean;
}

/**
 * A loading placeholder — NOT an empty state and NOT an error.
 *
 * CONTRACT §7 and UI rule 1: those are three different things and the Today
 * screen must never blur them. An empty Today screen that actually means "the
 * request failed" reads as "nothing to take", which is the one wrong answer
 * this app must never give.
 *
 * `aria-hidden` because a screen reader should hear the live region announce
 * "loading", not a dozen empty boxes.
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { className, circle = false, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={cn(
        'animate-pulse bg-raised',
        circle ? 'rounded-full' : 'rounded-md',
        className,
      )}
      {...props}
    />
  );
});

/** A few stacked lines, for a list that is still loading. */
export function SkeletonList({
  rows = 3,
  className,
  rowClassName,
}: {
  rows?: number;
  className?: string;
  rowClassName?: string;
}): JSX.Element {
  return (
    <div className={cn('flex flex-col gap-3', className)} role="status" aria-busy="true">
      <span className="sr-only">Loading</span>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className={cn('h-20 w-full', rowClassName)} />
      ))}
    </div>
  );
}
